import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, FileText } from 'lucide-react';
import eventService from '../services/eventService';
import useCustomAlert from '../hooks/useCustomAlert';
import CustomAlert from './CustomAlert';

const AdminEditEventModal = ({ show, onClose, eventReservation, onSuccess }) => {
  const { alert, showAlert, closeAlert } = useCustomAlert();
  const [formData, setFormData] = useState({
    numberOfParticipants: '',
    selectedDays: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (eventReservation) {
      // Parse selected days if exists
      let days = [];
      if (eventReservation.selectedDays) {
        try {
          days = JSON.parse(eventReservation.selectedDays);
        } catch (e) {
          console.error('Error parsing selected days:', e);
        }
      }

      setFormData({
        numberOfParticipants: eventReservation.numberOfParticipants || '',
        selectedDays: days
      });
    }
  }, [eventReservation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      
      // Só enviar campos que foram alterados
      if (formData.numberOfParticipants && 
          formData.numberOfParticipants !== eventReservation.numberOfParticipants) {
        updateData.NumberOfParticipants = parseInt(formData.numberOfParticipants);
      }

      if (formData.selectedDays && formData.selectedDays.length > 0) {
        updateData.SelectedDays = JSON.stringify(formData.selectedDays);
      }

      await eventService.adminUpdateEventReservation(eventReservation.reservationId || eventReservation.id, updateData);
      
      showAlert('Reserva de evento atualizada com sucesso!', 'success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      showAlert(error.message || 'Erro ao atualizar reserva de evento', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!show || !eventReservation) return null;

  return (
    <>
      <CustomAlert alert={alert} onClose={closeAlert} />
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal admin-edit-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Editar Reserva de Evento</h2>
            <button className="modal-close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            <div className="booking-info-summary event-summary">
              <h3>{eventReservation.eventTitle}</h3>
              <p className="booking-user">Cliente: {eventReservation.userName}</p>
              <div className="event-dates">
                <p>
                  <Calendar size={16} />
                  <span>Início: {new Date(eventReservation.eventStartDate).toLocaleDateString('pt-PT')}</span>
                </p>
                {eventReservation.eventEndDate && (
                  <p>
                    <Calendar size={16} />
                    <span>Término: {new Date(eventReservation.eventEndDate).toLocaleDateString('pt-PT')}</span>
                  </p>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="edit-booking-form">
              <div className="form-group">
                <label>
                  <Users size={18} />
                  Número de Participantes
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.numberOfParticipants}
                  onChange={(e) => setFormData({ ...formData, numberOfParticipants: e.target.value })}
                  required
                />
              </div>

              {formData.selectedDays && formData.selectedDays.length > 0 && (
                <div className="form-group">
                  <label>
                    <Calendar size={18} />
                    Dias Selecionados
                  </label>
                  <div className="selected-days-list">
                    {formData.selectedDays.map((day, index) => (
                      <div key={index} className="selected-day-item">
                        <Calendar size={14} />
                        <span>{new Date(day).toLocaleDateString('pt-PT')}</span>
                      </div>
                    ))}
                  </div>
                  <p className="form-help-text">
                    Para alterar os dias, contacte o cliente para fazer uma nova reserva
                  </p>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-close" 
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .admin-edit-modal {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .booking-info-summary {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          padding: 1.5rem;
          border-radius: 0.75rem;
          margin-bottom: 2rem;
          border-left: 4px solid #2563eb;
        }

        .event-summary {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-left: 4px solid #f59e0b;
        }

        .booking-info-summary h3 {
          margin: 0 0 0.5rem 0;
          color: #1e40af;
          font-size: 1.25rem;
        }

        .event-summary h3 {
          color: #92400e;
        }

        .booking-info-summary p {
          margin: 0.25rem 0;
          color: #64748b;
          font-size: 0.95rem;
        }

        .booking-user {
          font-weight: 600;
          color: #334155;
        }

        .event-dates {
          margin-top: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .event-dates p {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #92400e;
          font-weight: 500;
        }

        .edit-booking-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #334155;
          font-size: 0.95rem;
        }

        .form-group input,
        .form-group textarea {
          padding: 0.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .selected-days-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 0.5rem;
          border: 2px solid #e2e8f0;
        }

        .selected-day-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: white;
          border-radius: 0.375rem;
          border: 1px solid #cbd5e1;
          font-size: 0.875rem;
          color: #475569;
        }

        .form-help-text {
          font-size: 0.875rem;
          color: #64748b;
          font-style: italic;
          margin-top: 0.5rem;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e2e8f0;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        .btn-close {
          background: #f1f5f9;
          color: #64748b;
        }

        .btn-close:hover:not(:disabled) {
          background: #e2e8f0;
        }

        .btn-primary {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default AdminEditEventModal;